import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { sortByDate } from "@/lib/sortUtils";
import { ExportButton } from "@/components/ExportDialog";

const inputCls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";
const fmtINR = (n) => {
  const num = Number(n || 0);
  const [w, d = "00"] = num.toFixed(2).split(".");
  const neg = w.startsWith("-"); const s = neg ? w.slice(1) : w;
  let out = s;
  if (s.length > 3) { const last3 = s.slice(-3); let rest = s.slice(0, -3); const g = []; while (rest.length > 2) { g.unshift(rest.slice(-2)); rest = rest.slice(0, -2); } if (rest) g.unshift(rest); out = g.join(",") + "," + last3; }
  return (neg ? "-" : "") + out + "." + d;
};

const KINDS = { MR: { title: "Money Receipt", short: "MR", dir: "IN" }, PM: { title: "Paid Memo", short: "PM", dir: "OUT" } };

function FinanceForm({ kind, config, initial, onClose, onSaved }) {
  const isPM = kind === "PM";
  const [f, setF] = useState(initial || {
    issuer: isPM ? "BSPMT" : "BSPMT", date: new Date().toISOString().slice(0, 10),
    party_name: "", party_address: "", amount: "", payment_mode: "Cash",
    reference: "", expense_category: isPM ? "Transport" : "", purpose: "", remarks: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.party_name.trim()) return toast.error(isPM ? "Paid To is required" : "Received From is required");
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/finance/${kind}/${initial.id}`, f);
      else await api.post(`/finance/${kind}`, f);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{initial?.id ? "Edit" : "New"} {KINDS[kind].title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="overline text-muted-foreground">Issuer</Label>
            {isPM ? (
              <Input value="BSP MACHINES & TOOLS PVT. LTD. (BSPMT)" disabled data-testid="fin-issuer-locked" />
            ) : (
              <select className={inputCls} value={f.issuer} onChange={(e) => set("issuer", e.target.value)} data-testid="fin-issuer">
                {Object.entries(config.issuers || {}).map(([k, v]) => <option key={k} value={k}>{v} ({k})</option>)}
              </select>
            )}
          </div>
          <div className="space-y-1.5"><Label className="overline text-muted-foreground">Date</Label>
            <Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="fin-date" /></div>
          <div className="space-y-1.5"><Label className="overline text-muted-foreground">{isPM ? "Paid To" : "Received From"} *</Label>
            <Input value={f.party_name} onChange={(e) => set("party_name", e.target.value)} data-testid="fin-party" /></div>
          <div className="space-y-1.5"><Label className="overline text-muted-foreground">Payment Mode</Label>
            <select className={inputCls} value={f.payment_mode} onChange={(e) => set("payment_mode", e.target.value)} data-testid="fin-mode">
              {(config.payment_modes || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select></div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="overline text-muted-foreground">Address</Label>
            <Input value={f.party_address} onChange={(e) => set("party_address", e.target.value)} data-testid="fin-address" /></div>
          <div className="space-y-1.5"><Label className="overline text-muted-foreground">Amount (Rs.) *</Label>
            <Input type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="fin-amount" />
            {f.amount > 0 && <p className="text-xs text-muted-foreground">Rs. {fmtINR(f.amount)}</p>}</div>
          <div className="space-y-1.5"><Label className="overline text-muted-foreground">Reference (Cheque/Txn No.)</Label>
            <Input value={f.reference} onChange={(e) => set("reference", e.target.value)} data-testid="fin-reference" /></div>
          {isPM && (
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">Expense Category</Label>
              <select className={inputCls} value={f.expense_category} onChange={(e) => set("expense_category", e.target.value)} data-testid="fin-category">
                {(config.expense_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
          )}
          <div className="space-y-1.5 sm:col-span-2"><Label className="overline text-muted-foreground">{isPM ? "Being Payment For" : "Being Payment Towards"}</Label>
            <textarea className={inputCls} rows={2} value={f.purpose} onChange={(e) => set("purpose", e.target.value)} data-testid="fin-purpose" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="fin-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="fin-save">{saving && <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Finance() {
  const { can } = useAuth();
  const [kind, setKind] = useState("MR");
  const [config, setConfig] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ field: "date", dir: "desc" });
  const toggleSort = () => setSort((s) => ({ field: "date", dir: s.dir === "asc" ? "desc" : "asc" }));

  const load = useCallback(() => {
    if (!can("finance", "view")) { setLoading(false); return; }
    setLoading(true);
    api.get(`/finance/${kind}`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [kind]); // eslint-disable-line
  useEffect(() => { api.get("/finance/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const issue = async (row) => {
    if (!window.confirm(`Issue this ${KINDS[kind].title}? A number will be assigned and a Payment ${KINDS[kind].dir} ledger entry${kind === "PM" ? " + expense" : ""} will be created. This cannot be undone.`)) return;
    try { await api.post(`/finance/${kind}/${row.id}/issue`); toast.success("Issued"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Issue failed"); }
  };
  const preview = async (row) => {
    try { const r = await api.get(`/finance/${kind}/${row.id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("Could not generate PDF"); }
  };
  const removeDraft = async (row) => {
    if (!window.confirm(`Delete this draft ${KINDS[kind].title}? This cannot be undone.`)) return;
    try { await api.delete(`/finance/${kind}/${row.id}`); toast.success("Draft deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  if (!can("finance", "view"))
    return (<div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
      <Icons.Lock className="h-8 w-8 mx-auto text-destructive mb-3" />
      <h2 className="font-display text-xl font-bold">Access Restricted</h2>
      <p className="text-muted-foreground text-sm mt-2">You don't have the Finance permission.</p></div>);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#1e3a5f1a", color: "#1e3a5f" }}><Icons.ReceiptText className="h-5 w-5" /></div>
          <div><h1 className="font-display text-2xl font-bold tracking-tight">Finance</h1><p className="text-sm text-muted-foreground">Money Receipts &amp; Paid Memos · FY {config.fy || ""}</p></div>
        </div>
        <div className="flex flex-wrap gap-2">
        {can("finance", "export") && <ExportButton key={kind} report={kind === "MR" ? "money_receipts" : "paid_memos"} title={`${KINDS[kind].title} Register`} testid="fin-export-btn" />}
        {can("finance", "create") && <Button className="gap-1.5" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="fin-new-btn"><Icons.Plus className="h-4 w-4" />New {KINDS[kind].short}</Button>}
        </div>
      </div>

      <div className="flex gap-2">
        {Object.entries(KINDS).map(([k, v]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`fin-tab-${k}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${kind === k ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary/70"}`}>
            {v.title}s
          </button>
        ))}
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
            <TableHead className="overline text-muted-foreground whitespace-nowrap">Number</TableHead>
            <TableHead className="overline text-muted-foreground whitespace-nowrap">
              <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={toggleSort} data-testid="fin-sort-date">
                Date {sort.dir === "asc" ? <Icons.ArrowUp className="h-3 w-3" /> : <Icons.ArrowDown className="h-3 w-3" />}
              </button>
            </TableHead>
            {[kind === "PM" ? "Paid To" : "Received From", "Amount", "Issuer", "Status", ""].map((h, i) => <TableHead key={i} className="overline text-muted-foreground whitespace-nowrap">{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No {KINDS[kind].title.toLowerCase()}s yet.</TableCell></TableRow>}
            {!loading && sortByDate(items, sort.field, sort.dir).map((r) => (
              <TableRow key={r.id} className="hover:bg-secondary/40">
                <TableCell className="font-medium whitespace-nowrap">{r.number || "— draft —"}{r.source_module === "payment_ledger" && <Badge variant="secondary" className="ml-1.5 bg-violet-100 text-violet-800 text-[10px]" data-testid={`fin-from-ledger-${r.id}`}>FROM LEDGER</Badge>}{r.is_test && <Badge variant="secondary" className="ml-1.5 bg-amber-100 text-amber-800 text-[10px]">TEST</Badge>}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell>{r.party_name}</TableCell>
                <TableCell className="font-medium">Rs. {fmtINR(r.amount)}</TableCell>
                <TableCell><Badge variant="secondary">{r.issuer}</Badge></TableCell>
                <TableCell><Badge variant="secondary" className={r.status === "issued" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{r.status}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {r.status === "draft" && can("finance", "edit") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(r); setFormOpen(true); }} data-testid={`fin-edit-${r.id}`}><Icons.Pencil className="h-4 w-4" /></Button>}
                  {r.status === "draft" && can("finance", "create") && <Button size="sm" variant="outline" className="ml-1 gap-1" onClick={() => issue(r)} data-testid={`fin-issue-${r.id}`}><Icons.CheckCircle2 className="h-3.5 w-3.5" />Issue</Button>}
                  {can("finance", "download") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => preview(r)} data-testid={`fin-pdf-${r.id}`}><Icons.FileText className="h-4 w-4" /></Button>}
                  {r.status === "draft" && can("finance", "delete") && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeDraft(r)} data-testid={`fin-delete-${r.id}`}><Icons.Trash2 className="h-4 w-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {formOpen && <FinanceForm kind={kind} config={config} initial={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
    </div>
  );
}
